/**
 * The single place attendance turns clocks and timestamps into the strings a
 * user sees and the API is queried with.
 *
 * This module exists because attendance date handling had been reimplemented
 * inline in every component that needed it - `SelfCheckInCard`, the ESS
 * dashboard and `format.ts` each grew their own `todayISO()` - and each copy
 * had to independently avoid the same trap:
 *
 *     new Date().toISOString().slice(0, 10)   // WRONG for attendance
 *
 * `toISOString()` converts to UTC first. In any positive-offset timezone
 * (IST is UTC+5:30) that returns YESTERDAY's calendar date for the first
 * 5.5 hours of every day, and in a negative-offset timezone it returns
 * TOMORROW's late in the evening. Applied to attendance it means the
 * employee's own screen queries a different business day than the one their
 * check-in was written under, so a punch that persisted perfectly well looks
 * like it never happened.
 *
 * Every attendance surface - employee and Tenant Admin alike - must derive
 * its business date from here, so both are always asking the backend about
 * the same day. The backend's own counterpart is
 * `AttendanceService.location` / `dateutil.FromTime`, which resolves the
 * business date in the TENANT's timezone; this file resolves it in the
 * viewer's local timezone, which is the same day for anyone working in the
 * tenant's own country and is the closest the browser can get without
 * shipping a timezone library.
 *
 * See also `.eslintrc.json`, which fails the build if attendance code
 * reintroduces a raw `toISOString()` date slice.
 */

/** A business date in the API's wire format, "YYYY-MM-DD". */
export type BusinessDate = string;

/**
 * Formats a Date as its LOCAL calendar date - no UTC round trip anywhere.
 * This is the one conversion the whole module is built on.
 */
export function toBusinessDate(date: Date): BusinessDate {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/** Today's business date. The default `from`/`to` for any "today" query. */
export function businessToday(): BusinessDate {
  return toBusinessDate(new Date());
}

/** The business date an ISO timestamp from the API belongs to, in local time. */
export function businessDateOf(timestamp: string): BusinessDate {
  return toBusinessDate(new Date(timestamp));
}

/** The `{ from, to }` range covering only today - the shape the API expects. */
export function todayRange(): { from: BusinessDate; to: BusinessDate } {
  const today = businessToday();
  return { from: today, to: today };
}

/**
 * The `{ from, to }` range covering a whole "YYYY-MM" period, month length
 * included. Built from the period string itself rather than from a Date, so
 * it cannot drift across a timezone boundary.
 *
 * Never extends past today: for the CURRENT month, `to` is narrowed down to
 * today (a future date has not happened yet, so there is nothing to report
 * for it - see this module's own doc comment and the backend's identical
 * clamp in AttendanceService.Recalculate/ReportService.AttendanceReport). A
 * FUTURE month returns `to` before `from`, which every attendance query this
 * feeds reads as "nothing in range" rather than a value that needs its own
 * special case. A completed PAST month is returned unchanged, in full.
 *
 * This is a client-side convenience only, matching the backend's own
 * behaviour so the UI does not ask for dates the API would refuse anyway -
 * the backend remains the final authority and enforces this independently.
 */
export function monthRange(period: string): { from: BusinessDate; to: BusinessDate } {
  const [year, month] = period.split("-").map(Number);
  const lastDay = new Date(year!, month!, 0).getDate();
  const from = `${period}-01`;
  const to = `${period}-${String(lastDay).padStart(2, "0")}`;

  const today = businessToday();
  const todayPeriod = today.slice(0, 7);
  if (period === todayPeriod) {
    return { from, to: today };
  }
  if (period > todayPeriod) {
    // Entirely future - `from` (the 1st of that month) is guaranteed to be
    // after `today` here, so returning `to: today` puts `to` before `from`,
    // which every attendance query this feeds reads as "nothing to show" -
    // the same signal the backend's own clamp produces.
    return { from, to: today };
  }
  return { from, to };
}

/**
 * Renders a punch/attendance timestamp as a clock time.
 *
 * The value passed in is always a SERVER timestamp - the backend is the
 * source of truth for when a punch happened (`SelfPunchOutcome.punch_time`,
 * `AttendanceRecord.first_in`/`last_out`). This only formats it; it never
 * substitutes the device clock, and it returns an em dash rather than
 * "Invalid Date" for a missing value.
 */
export function formatPunchTime(timestamp: string | null | undefined): string {
  if (!timestamp) return "—";
  const parsed = new Date(timestamp);
  if (Number.isNaN(parsed.getTime())) return "—";
  return parsed.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
}

/**
 * Renders a persisted `worked_minutes` value as "8h 50m".
 *
 * The number ALWAYS comes from the backend's settled attendance record - the
 * UI never derives working time by subtracting timestamps itself or by
 * running a timer against the device clock, which is what let the employee
 * screen and the Tenant Admin table disagree. A negative value can only mean
 * corrupt data and is rendered as zero rather than as "-1h 10m".
 */
export function formatWorkedMinutes(minutes: number | null | undefined): string {
  if (minutes == null || Number.isNaN(minutes) || minutes <= 0) return "0h 0m";
  const whole = Math.round(minutes);
  return `${Math.floor(whole / 60)}h ${whole % 60}m`;
}

/**
 * The late badge text for a day, from the engine's own late_minutes (first
 * check-in less shift start less grace). null when the day was on time, so
 * callers render no badge at all rather than a bare "Late".
 */
export function formatLateBy(record: { is_late: boolean; late_minutes: number }): string | null {
  if (!record.is_late || !(record.late_minutes > 0)) return null;
  const m = Math.round(record.late_minutes);
  return m < 60 ? `Late by ${m}m` : `Late by ${Math.floor(m / 60)}h ${m % 60}m`;
}

/**
 * Renders the Working time cell for a settled attendance row.
 *
 * A day flagged `is_missing_checkout` has an open check-in whose shift has
 * already ended, so the engine has no closing punch to measure against and
 * `worked_minutes` settles at 0. Rendering that as "0h 0m" states something
 * false - the employee worked a full day, the system just has no evidence of
 * when they stopped - and it reads identically to a genuine no-show. "Pending
 * correction" says what is actually true and points at the fix.
 *
 * Only the zero case is substituted: a day with more than one punch pair where
 * the LAST pair is left open still has real measured time from the completed
 * pairs, and that number is worth showing.
 */
export function formatWorkedTimeCell(record: {
  worked_minutes: number;
  is_missing_checkout?: boolean;
}): string {
  if (record.is_missing_checkout && !(record.worked_minutes > 0)) return "Pending correction";
  return formatWorkedMinutes(record.worked_minutes);
}
